import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { MemberResource, PluginOrderResource } from '~~apis/resource';
import Button from '~~elements/Button';
import Input from '~~elements/Input';
import Modal from '~~elements/Modal';
import { StyledTableWrapper, DisplayBody } from '~~elements/Styled';
import { getNameDisplay, PHONE_WIDTH } from '~~elements/Const';
import toast from '~~elements/Toast';
import moment from 'moment';
import { useRootDispatch } from '../../useRoot';
import { ROOT_ACTION } from '../../actionTypes';

const StyledDiv = styled.div`
  .table-wrapper {
    td,
    button {
      white-space: nowrap;
    }
  }

  @media (max-width: ${PHONE_WIDTH}) {
    .table-wrapper {
      overflow-x: auto;
      table {
        width: 100%;
        display: block;
      }
      td {
        padding: 0.25rem 0.25rem;
      }
    }
  }
`;

const StyledModalBody = styled.div`
  text-align: start;
  small {
    color: #00000080;
  }
  p {
    margin: 0;
  }
  hr {
    border-top: 1px solid gray;
  }
`;

const StyledModalFooter = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.8rem;
  input {
    width: 8rem;
  }
`;

const receiptTypes = {
  PERSONAL: '個人',
  DONATE: '捐贈',
  COMPANY: '公司（統編）',
};

const paymentType = {
  ECPAY: '綠界',
};

const MemberManagement = () => {
  const rootDispatch = useRootDispatch();

  const [members, setMembers] = useState([]);

  const [selectedMemberIndex, setSelectedMemberIndex] = useState(-1);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [receiptCreatedAt, setReceiptCreatedAt] = useState('');

  useEffect(() => {
    MemberResource.list().then(({ data }) => {
      setMembers(data);
    });
  }, []);

  const setSelectedMember = (index) => {
    if (!members[index]) {
      return;
    }

    const member = members[index];

    setSelectedMemberIndex(index);
    setReceiptNumber(!member.order ? '' : member.order.receipt_number);
    setReceiptCreatedAt(
      !member.order
        ? ''
        : !member.order.receipt_created_at
        ? ''
        : moment(member.order.receipt_created_at).format('YYYY-MM-DD'),
    );
  };

  const handleUpdateReceipt = () => {
    if (!members[selectedMemberIndex]?.order) {
      return;
    }

    if (receiptNumber.trim() !== '' && receiptCreatedAt.trim() === '') {
      toast('請設定發票日期', { status: 'error', second: 1 });
      return;
    }

    rootDispatch({ type: ROOT_ACTION.SET_LOADING, payload: true });

    PluginOrderResource.updatePluginOrderReceipt({
      pluginOrderUniqid: members[selectedMemberIndex].order.uniqid,
      data: {
        receipt_number: receiptNumber.trim(),
        receipt_created_at: !receiptCreatedAt.trim() ? '' : moment(receiptCreatedAt.trim()).format('YYYY-MM-DD'),
      },
    })
      .then(({ data: newPluginOrder }) => {
        toast('更新成功！', { status: 'success', second: 1 });
        members[selectedMemberIndex].order = newPluginOrder;
      })
      .catch((e) => {
        console.error(e);
        toast('更新失敗！', { status: 'error', second: 1 });
      })
      .finally(() => {
        rootDispatch({ type: ROOT_ACTION.SET_LOADING, payload: false });
      });
  };

  return (
    <StyledDiv>
      <DisplayBody>
        <h1>會員管理頁面</h1>
        <StyledTableWrapper className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>流水</th>
                <th>帳戶</th>
                <th>信箱</th>
                <th>姓名</th>
                <th>支持球隊</th>
                <th>效期</th>
                <th>訂單編號</th>
                <th>發票</th>
                <th>詳細</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member, memberIndex) => (
                <tr key={memberIndex}>
                  <td>{memberIndex + 1}</td>
                  <td>{member.user.user_name}</td>
                  <td>{member.user.email}</td>
                  <td>{getNameDisplay(member.user.first_name, member.user.last_name)}</td>
                  <td>{member.major_team}</td>
                  <td>
                    {moment(member.start_at).format('YYYY-MM-DD')}
                    {` - `}
                    {moment(member.end_at).format('YYYY-MM-DD')}
                  </td>
                  <td>{!member.order ? '' : member.order.uniqid}</td>
                  <td>{!member.order ? '' : !member.order.receipt_number ? '未開立' : member.order.receipt_number}</td>
                  <td>
                    <Button
                      color={member.order && !member.order.receipt_number ? 'red' : 'blue'}
                      mini
                      onClick={() => setSelectedMember(memberIndex)}
                      disabled={!member.order}
                    >
                      詳細
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </StyledTableWrapper>
      </DisplayBody>
      {members[selectedMemberIndex] && (
        <Modal.Modal
          key={selectedMemberIndex}
          isOpen={true}
          onClickBack={() => setSelectedMemberIndex(-1)}
          outterClickBack={false}
        >
          <Modal.ModalHeader hasBack={true} onClickBack={() => setSelectedMemberIndex(-1)}>
            {members[selectedMemberIndex].user.user_name}
          </Modal.ModalHeader>
          <Modal.ModalBody>
            <StyledModalBody>
              {members[selectedMemberIndex].order && (
                <>
                  <small>訂單</small>
                  <p>{members[selectedMemberIndex].order.uniqid}</p>
                  <small>發票人</small>
                  <p>
                    {members[selectedMemberIndex].order.buyer_name}({members[selectedMemberIndex].order.buyer_phone})
                    {members[selectedMemberIndex].order.buyer_tel &&
                      `(${members[selectedMemberIndex].order.buyer_tel})`}
                  </p>
                  <p>{`${members[selectedMemberIndex].order.buyer_city} ${members[selectedMemberIndex].order.buyer_postcode} ${members[selectedMemberIndex].order.buyer_street}`}</p>
                  <hr />
                  <small>發票資訊</small>
                  <p>{receiptTypes[members[selectedMemberIndex].order.receipt_type]}</p>
                  <p>
                    {members[selectedMemberIndex].order.receipt_number}(
                    {!members[selectedMemberIndex].order.receipt_created_at
                      ? '尚未開立'
                      : moment(members[selectedMemberIndex].order.receipt_created_at).format('YYYY-MM-DD')}
                    )
                  </p>
                  <hr />
                  <small>訂單內容</small>
                  {members[selectedMemberIndex].order.items.map((item, i) => (
                    <p>
                      {`${i + 1}. ${item.name} `}
                      {`NT$ ${new Intl.NumberFormat('zh-TW').format(item.retail_price)} `}
                      {`x${item.amount}`}
                    </p>
                  ))}
                  <small>付款資訊</small>
                  <p>
                    {paymentType[members[selectedMemberIndex].order.payment_type]}(
                    {members[selectedMemberIndex].order.payment_id})
                  </p>
                  <p>{`NT$ ${new Intl.NumberFormat('zh-TW').format(
                    members[selectedMemberIndex].order.payment_amount,
                  )}`}</p>
                  <p>{moment(members[selectedMemberIndex].order.paid_at).format('YYYY-MM-DD')}</p>
                  <hr />
                  <small>用戶備註</small>
                  <p>
                    {!members[selectedMemberIndex].order.comment ? '無' : members[selectedMemberIndex].order.comment}
                  </p>
                </>
              )}
            </StyledModalBody>
          </Modal.ModalBody>
          <Modal.ModalFooter>
            <StyledModalFooter>
              <Input placeholder="發票號碼" value={receiptNumber} onChange={(e) => setReceiptNumber(e.target.value)} />
              <Input type="date" value={receiptCreatedAt} onChange={(e) => setReceiptCreatedAt(e.target.value)} />
              <Button mini color="red" width="fit-content" onClick={handleUpdateReceipt}>
                更新發票
              </Button>
            </StyledModalFooter>
          </Modal.ModalFooter>
        </Modal.Modal>
      )}
    </StyledDiv>
  );
};

export default MemberManagement;
